import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Modifier, usePopper } from 'react-popper'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'
import { useClickOutside } from 'shared/hooks/use-click-outside'
import { useDebouncedValue } from 'shared/hooks/use-debounced-value'
import SearchIcon from 'shared/icons/search-icon'
import { useCommunitySearchSuggestions } from 'modules/community/api/get-community-search-suggestions'
import { buildSearchUrl } from './utils'

interface FormData {
  search: string
}

interface Props {
  className?: string
  placeholder?: string
}

export function CommunitySearchForm({ className, placeholder }: Props) {
  const router = useRouter()
  const { communityPath, topicPath } = router.query as { communityPath: string; topicPath?: string }
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const [activeIndex, setActiveIndex] = useState<number>(-1)

  const rawSameWidth: Modifier<'sameWidth', {}> = {
    name: 'sameWidth',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
      state.styles.popper.width = `${state.rects.reference.width}px`
    },
    effect: ({ state }) => {
      state.elements.popper.style.width = `${state.elements.reference.getBoundingClientRect().width}px`
    },
  }

  // Memoize modifier to prevent infinite render loop in Popper
  const sameWidth = useMemo(() => rawSameWidth, [])

  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<FormData>({ defaultValues: { search: '' } })
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: { offset: [0, 15] },
      },
      sameWidth,
    ],
  })

  const [debouncedSearch] = useDebouncedValue(watch('search'), 300)
  const { data: suggestions } = useCommunitySearchSuggestions({
    path: communityPath,
    topicPath: topicPath,
    // query: isValid ? debouncedSearch : undefined,
    query: undefined,
  })

  const error = errors.search?.message
  const handleClickOutside = useCallback(() => setIsOpen(false), [setIsOpen])
  const formRef = useClickOutside(handleClickOutside)

  useEffect(() => {
    if (router.asPath.includes('/search') && router.query.q) {
      setValue('search', router.query.q as string)
    }
  }, [router.asPath, router.query.q, setValue])

  useEffect(() => {
    setActiveIndex(-1)
  }, [suggestions, isOpen, debouncedSearch])

  const onSubmit = ({ search }: FormData) => {
    router.push(buildSearchUrl(search, communityPath, topicPath))
  }

  // const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
  //   if (!suggestions?.length) return

  //   if (event.key === 'ArrowDown') {
  //     event.preventDefault()
  //     setIsOpen(true)
  //     setActiveIndex(prev => (prev + 1) % suggestions.length)
  //     return
  //   }

  //   if (event.key === 'ArrowUp') {
  //     event.preventDefault()
  //     setIsOpen(true)
  //     setActiveIndex(prev => (prev <= 0 ? suggestions.length - 1 : prev - 1))
  //     return
  //   }

  //   if (event.key === 'Enter' && activeIndex >= 0) {
  //     event.preventDefault()
  //     const selected = suggestions[activeIndex]
  //     setValue('search', selected)
  //     router.push(buildSearchUrl(selected, communityPath, topicPath))
  //     setIsOpen(false)
  //   }
  // }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(onSubmit)}
      className={twMerge(
        'flex flex-col gap-3 rounded-md bg-background-secondary p-5 lg:px-0',
        className,
      )}
    >
      <div className="relative lg:px-5" ref={setReferenceElement}>
        <input
          onFocus={() => setIsOpen(true)}
          // onKeyDown={handleKeyDown}
          type="search"
          placeholder={placeholder ?? t('global.search')}
          className="hide-webkit-search-cancel-button max-h-12 w-full rounded-md border border-text-secondary/30 p-4 pr-9 text-text-primary focus:border-brand-primary focus:outline-none lg:max-h-10"
          {...register('search', {
            minLength: { value: 3, message: 'community.search.errors.min_length' },
          })}
        />
        <button
          type="submit"
          title={t('global.search')}
          className="absolute right-7 top-1/2 -translate-y-1/2"
        >
          <SearchIcon className="size-5 text-text-primary" />
        </button>
      </div>
      {/* {!!error && <p className="text-sm text-danger">{t(error)}</p>}
      {isOpen && suggestions && Boolean(suggestions?.length) && (
        <div
          ref={setPopperElement}
          className="z-30"
          style={{ ...styles.popper }}
          {...attributes.popper}
        >
          <div className="min-w-60 rounded-lg bg-background-secondary py-5 shadow-[0px_4px_10px_0px_#00000040]">
            <ul className="flex flex-col gap-2">
              {suggestions.map((suggestion, index) => (
                <li key={suggestion} className="w-full">
                  <Link
                    href={buildSearchUrl(suggestion, communityPath, topicPath)}
                    className={twMerge(
                      'inline-block w-full cursor-pointer pl-5 text-text-primary hover:bg-background-primary',
                      activeIndex === index && 'bg-background-primary',
                    )}
                    onClick={() => setIsOpen(false)}
                  >
                    {suggestion}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )} */}
    </form>
  )
}
